import React, { useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

interface IAccordian {
  title: string;
  rightIcons?: React.ReactNode;
  children: React.ReactNode;
  isExpanded?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

export const EnhancedAccordian = (props: IAccordian) => {
  const { title, rightIcons = [], children, isExpanded, onToggle } = props;
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = isExpanded !== undefined;
  const expanded = isControlled ? isExpanded : internalOpen;

  const handleChange = (_event: React.SyntheticEvent, newExpanded: boolean) => {
    if (!isControlled) {
      setInternalOpen(newExpanded);
    }
    onToggle?.(newExpanded);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      disableGutters
      elevation={0}
      sx={{
        mb: 1.5,
        borderRadius: '12px !important',
        border: '1px solid',
        borderColor: expanded ? 'rgba(16, 185, 129, 0.4)' : 'divider',
        bgcolor: 'background.paper',
        boxShadow: expanded ? '0 4px 12px rgba(16, 185, 129, 0.08)' : '0 1px 3px rgba(0, 0, 0, 0.02)',
        transition: 'all 0.2s ease',
        '&:before': {
          display: 'none',
        },
        '&:hover': {
          borderColor: expanded ? 'rgba(16, 185, 129, 0.5)' : 'rgba(16, 185, 129, 0.25)',
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreRoundedIcon
            sx={{
              color: expanded ? 'primary.main' : 'text.secondary',
              transition: 'transform 0.2s ease',
            }}
          />
        }
        sx={{
          minHeight: 46,
          px: 2,
          py: 0.5,
          bgcolor: expanded ? 'rgba(16, 185, 129, 0.04)' : 'transparent',
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'divider',
          borderRadius: expanded ? '12px 12px 0 0' : '12px',
          '& .MuiAccordionSummary-content': {
            my: 0.5,
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 4,
              height: 18,
              borderRadius: '9999px',
              bgcolor: expanded ? 'primary.main' : 'divider',
              transition: 'background-color 0.2s ease',
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
        </Box>

        {rightIcons && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          >
            {React.Children.map(rightIcons, (icon, idx) => {
              const variant = (icon as any)?.props?.['data-variant'];
              const isDanger = variant === 'danger';
              const handler = (icon as any)?.props?.onClick;
              return (
                <IconButton
                  key={idx}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handler?.(e);
                  }}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    color: isDanger ? 'error.main' : 'text.secondary',
                    bgcolor: isDanger ? 'rgba(239, 68, 68, 0.06)' : 'transparent',
                    '&:hover': {
                      bgcolor: isDanger ? 'rgba(239, 68, 68, 0.14)' : 'action.hover',
                    },
                    '& svg': {
                      width: 16,
                      height: 16,
                    },
                  }}
                >
                  {icon}
                </IconButton>
              );
            })}
          </Box>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, bgcolor: 'background.paper', borderRadius: '0 0 12px 12px' }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

